import {
  FulfillmentTypeListResponseType,
  StockListResponseType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  findProductFulfillmentTypeDeliveryCharge,
  formatCurrency,
  isIneligibleForFulfillmentType,
  isOrderItemEligibleForDeliveryMethod,
} from "@sellar-io/common";
import { ReactElement, ReactNode, useState } from "react";
import { useMatch } from "react-router";
import { useSearchParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { StockIndicator } from "../../../../../components";
import {
  SkeletonContainer,
  SkeletonText,
} from "../../../../../components/skeleton/Skeleton";
import {
  TableLegacyTR,
  TableLegacyTD,
} from "../../../../../components/table/TableLegacy";
import { Popover } from "../../../../../components/popover/Popover";
import { PopoverContent } from "../../../../../components/popover/PopoverContent";
import { PopoverTrigger } from "../../../../../components/popover/PopoverTrigger";
import { STOREFRONT_LINKS_PREVIEW } from "../../../../../constants/routes";
import { useSellarUser } from "../../../../../hooks";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { useStorefrontLinkSearchStore } from "../../../../storefrontLinks/StorefrontLinkSearchStore";
import { DiscountedPrice } from "../../../DiscountedPrice";
import CategoryPageDietaryTypes from "../../CategoryPageDietaryTypes";
import { StorefrontProductCard } from "../StorefrontLayout/StorefrontProductCard";
import { AvailableFromBadge } from "../../../../../components/badge/AvailableFromBadge";
import { useSignupContext } from "../../../../../hooks/useSignupContext.shared";
import { useLocalStorage } from "usehooks-ts";
import { generateSignupContinueUrl } from "../../../../../utils/sellarHelpers";
import { SignUpToOrderTooltip } from "../SignUpToOrderTooltip/SignUpToOrderTooltip";
import { CheckoutSelectedFulfillmentType } from "../../..";
import { SwitchDeliveryMethodDialog } from "./Dialog/SwitchDeliveryMethodDialog";
import { DataButton, Icon, H6 } from "@sellar-io/components";
import { useBasketContext } from "../../../../../hooks";
import { isValidContainerUnit } from "../../../Components/DeliveryMethodRadioSelector.shared";

const formatDeliveryCharge = (deliveryCharge: number | undefined) => {
  if (deliveryCharge === undefined) {
    return "";
  }

  if (deliveryCharge === 0) {
    return "Free";
  }

  return formatCurrency(deliveryCharge, "GBP");
};

export const StorefrontTableProductRow = ({
  item,
  child,
  onClick,
  supplier,
  stock,
  fulfillmentTypeContainerDeliveryCharges,
  children,
  selectedFulfillmentType,
  courierFulfillmentType,
  supplierStorefront,
  onFulfillmentTypeChange,
  previewDisabled = false,
}: {
  item: StockListResponseType[number];
  child: StockListResponseType[number]["Products"][number];
  onClick?: () => void;
  supplier: SupplierGetResponseType;
  stock: number;
  fulfillmentTypeContainerDeliveryCharges?: {
    volume: number;
    packQuantity: number;
    displayUnit: string;
    containerType: string;
    charge: number;
  }[];
  children?: ReactNode;
  selectedFulfillmentType?: CheckoutSelectedFulfillmentType;
  courierFulfillmentType: FulfillmentTypeListResponseType[number] | undefined;
  supplierStorefront?: SupplierStorefrontV2GetResponseType;
  onFulfillmentTypeChange?: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  previewDisabled?: boolean;
}): ReactElement => {
  if (!supplier) throw new Error("Cannot find supplier");

  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const [{ order }] = useBasketContext();
  const [isSwitchDeliveryDialogActive, setIsSwitchDeliveryDialogActive] =
    useState(false);
  const unbundlingEnabled =
    (supplierStorefront?.deliveryMethods?.length ?? 0) > 0;
  const [searchParams] = useSearchParams();
  const { selectedAudienceTab } = useStorefrontLinkSearchStore();
  const storefrontLink =
    searchParams.get("storefrontLink") || searchParams.get("priceTierLink");
  const orderFlowToken = searchParams.get("orderFlowToken");

  const isLoggedIn = !!business && !!user;
  const getDeliveryCharge = (
    product: StockListResponseType[number]["Products"][number],
  ) => {
    return findProductFulfillmentTypeDeliveryCharge({
      product: {
        ...product,
        displayUnit: product.unit,
      },
      fulfillmentTypeContainerDeliveryCharges,
    });
  };

  const productItemDeliveryCharge = getDeliveryCharge(child);

  const formattedDeliveryCharge = formatDeliveryCharge(
    productItemDeliveryCharge?.charge,
  );

  const currentDeliveryMethod = supplierStorefront?.deliveryMethods?.find(
    (dm) => dm.fulfillmentType === selectedFulfillmentType,
  );

  const availableDeliveryMethods =
    supplierStorefront?.deliveryMethods?.filter((deliveryMethod) => {
      if (
        !child.containerType ||
        !deliveryMethod.DeliveryMethodContainerRules ||
        !isValidContainerUnit(child.unit)
      ) {
        return true;
      }

      return isOrderItemEligibleForDeliveryMethod({
        deliveryMethod,
        item: { ...child, unit: child.unit },
      });
    }) ?? [];

  const orderItemIsEligible = availableDeliveryMethods.some(
    (deliveryMethod) =>
      deliveryMethod.fulfillmentType === selectedFulfillmentType,
  );

  const hasContainerType = Boolean(child.containerType);
  const hasCurrentDeliveryMethod = Boolean(currentDeliveryMethod);
  const isUnavailableForCurrentDeliveryMethod =
    unbundlingEnabled &&
    hasContainerType &&
    hasCurrentDeliveryMethod &&
    !orderItemIsEligible;

  const hasAvailableDeliveryMethods =
    availableDeliveryMethods.length > 0 || supplier.allowCollectionOnCheckout;

  const palletOnly =
    !unbundlingEnabled &&
    isIneligibleForFulfillmentType({
      fulfillmentType: courierFulfillmentType,
      item: child,
    }) &&
    selectedFulfillmentType === "courier";

  const isStorefrontLinkPreview = !!useMatch(STOREFRONT_LINKS_PREVIEW);

  const showPrice =
    (isLoggedIn || storefrontLink || orderFlowToken) &&
    child.ProductPriceTier &&
    child.ProductPriceTier.price >= 0;

  const showDeliverySkeleton =
    (isStorefrontLinkPreview || (storefrontLink && !showPrice)) &&
    selectedAudienceTab === "all";

  const { setSignupDialogActive } = useSignupContext();
  const [, setOnboardingContinueUrl] = useLocalStorage(
    "onboardingContinueUrl",
    "",
  );

  const signUpToOrderClick = () => {
    if (isStorefrontLinkPreview) return;

    const continueSearchParams = generateSignupContinueUrl(
      supplier.id,
      storefrontLink ? `storefrontLink=${storefrontLink}` : "",
    );
    setOnboardingContinueUrl(
      `${continueSearchParams.get("continue")}${
        continueSearchParams.get("queryParameters")
          ? `?${continueSearchParams.get("queryParameters")}`
          : ""
      }`,
    );

    setSignupDialogActive(true);
  };

  if (!hasAvailableDeliveryMethods && unbundlingEnabled) {
    return <></>;
  }

  return (
    <>
      <TableLegacyTR
        className={twMerge(
          "min-w-37.5 text-sm",
          onClick && "cursor-pointer hover:bg-zinc-50",
          isUnavailableForCurrentDeliveryMethod && "text-zinc-400",
        )}
      >
        <TableLegacyTD onClick={onClick}>
          <Popover open={previewDisabled ? false : undefined}>
            <div
              className={twMerge(
                "relative flex flex-none flex-col gap-1",
                isUnavailableForCurrentDeliveryMethod && "pb-8",
              )}
            >
              <PopoverTrigger className="line-clamp-2">
                {child.name?.split(" • ").slice(1).join(" • ")}
              </PopoverTrigger>
              {child.ProductPriceTier?.discountable === false && (
                <H6 className="text-xs">
                  This item is not eligible for volume discounts.
                </H6>
              )}
              {!!child.availableFrom && (
                <AvailableFromBadge availableFrom={child.availableFrom} />
              )}
              {isUnavailableForCurrentDeliveryMethod && (
                <span className="absolute bottom-0 flex items-center gap-1 text-xs text-nowrap text-zinc-600">
                  <Icon
                    icon="INFORMATION_OUTLINE"
                    className="flex-none"
                    size={0.5}
                  />
                  Not available with this delivery type
                </span>
              )}
            </div>
            {onClick && (
              <PopoverContent className="w-96 max-w-[min(24rem,calc(100vw-16px))]">
                <StorefrontProductCard
                  className="min-w-0 overflow-hidden pt-0"
                  item={item}
                  supplier={supplier}
                />
              </PopoverContent>
            )}
          </Popover>
        </TableLegacyTD>
        <TableLegacyTD onClick={onClick}>
          <CategoryPageDietaryTypes child={child} parent={item} />
        </TableLegacyTD>
        <TableLegacyTD onClick={onClick}>
          {" "}
          <StockIndicator
            stock={stock}
            canBackorder={child.canBackorder}
            type={supplier.showStock ? "short" : "minimal"}
          />
        </TableLegacyTD>

        <TableLegacyTD>
          {showPrice ? (
            <div onClick={onClick}>
              <DiscountedPrice productPriceTier={child.ProductPriceTier} />
            </div>
          ) : (
            <SkeletonContainer
              className={"flex items-center justify-center"}
              onClick={() => (business ? onClick?.() : signUpToOrderClick())}
            >
              <SignUpToOrderTooltip enable={!isStorefrontLinkPreview}>
                <div className="flex items-center gap-1">
                  {isStorefrontLinkPreview ? "" : "£"}
                  <SkeletonText
                    className={
                      isStorefrontLinkPreview
                        ? "w-[75px]"
                        : "h-6 w-full bg-zinc-100"
                    }
                  />
                </div>
              </SignUpToOrderTooltip>
            </SkeletonContainer>
          )}
        </TableLegacyTD>
        <TableLegacyTD>
          {isUnavailableForCurrentDeliveryMethod ? (
            <H6 className="text-zinc-400">N/A</H6>
          ) : selectedFulfillmentType === "sellarDistribution" &&
            !showDeliverySkeleton ? (
            <H6>FREE</H6>
          ) : (formattedDeliveryCharge || palletOnly) &&
            !showDeliverySkeleton ? (
            <div onClick={onClick}>
              <H6>{palletOnly ? "Pallet only" : formattedDeliveryCharge}</H6>
            </div>
          ) : (
            showDeliverySkeleton && (
              <SkeletonContainer
                className={"flex items-center justify-center"}
                onClick={signUpToOrderClick}
              >
                <SignUpToOrderTooltip enable={!isStorefrontLinkPreview}>
                  <div className="flex items-center gap-1">
                    {isStorefrontLinkPreview ? "" : "£"}
                    <SkeletonText
                      className={
                        isStorefrontLinkPreview
                          ? "w-[75px]"
                          : "h-6 w-full bg-zinc-100"
                      }
                    />
                  </div>
                </SignUpToOrderTooltip>
              </SkeletonContainer>
            )
          )}
        </TableLegacyTD>
        {(showPrice || isLoggedIn) && (
          <>
            {isUnavailableForCurrentDeliveryMethod ? (
              <TableLegacyTD>
                <DataButton
                  border={true}
                  onClick={() => setIsSwitchDeliveryDialogActive(true)}
                  className="flex items-center gap-2 text-xs text-zinc-800"
                >
                  <Icon icon="SWAP_VERTICAL" />
                  Switch delivery
                </DataButton>
              </TableLegacyTD>
            ) : (
              children
            )}
          </>
        )}
      </TableLegacyTR>
      {unbundlingEnabled &&
        supplierStorefront &&
        hasAvailableDeliveryMethods &&
        onFulfillmentTypeChange && (
          <SwitchDeliveryMethodDialog
            active={isSwitchDeliveryDialogActive}
            onDismiss={() => setIsSwitchDeliveryDialogActive(false)}
            supplier={supplier}
            supplierStorefront={supplierStorefront}
            currentFulfillmentType={selectedFulfillmentType}
            availableDeliveryMethods={availableDeliveryMethods}
            onFulfillmentTypeChange={onFulfillmentTypeChange}
            order={order}
            productName={child.name ? child.name.split(" • ").join(" - ") : ""}
            productItem={child}
          />
        )}
    </>
  );
};
